import React, { useEffect, useRef, useState } from 'react';
import { GlobeAltIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useDebounce } from './useDebounce';

const ipDelimiter = /[, ]+/; // Comma or a space
const isValidIp = (ipString: string) => {
  const ipArray = ipString.split(ipDelimiter);
  const regex = new RegExp('^((25[0-5]|(2[0-4]|1\\d|[1-9]|)\\d)\\.?\\b){4}$');
  return ipArray.every((ip) => regex.test(ip.trim()));
};

const IpInput = ({
  ipValue = '',
  setIpValue,
  placeholder,
}: {
  ipValue?: string;
  setIpValue: (term: string) => void;
  placeholder: string;
}) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setIpValue(value);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      setIpValue(ipValue);
    }
  };
  return (
    <input
      type="text"
      placeholder={placeholder}
      className={`w-full border-0 bg-transparent focus:ring-0`}
      value={ipValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
    />
  );
};

export const ElasticIpFilter = ({
  placeholder,
  setValue,
  value,
  clearCount,
  justifyCenter = true,
  showClear,
  wrapperClassName,
  dataTestId,
}: {
  placeholder: string;
  setValue: (value: string[]) => void;
  value?: string[];
  /** Changes when the filter is emptied from outside, e.g. by Clear all. */
  clearCount?: number;
  justifyCenter?: boolean;
  showClear?: boolean;
  wrapperClassName?: string;
  dataTestId?: string;
}) => {
  const [isValid, setIsValid] = useState(true);
  const [ip, setIp] = useState(value?.toString());
  const debouncedIpValue = useDebounce(ip);
  // Callers pass a new array on every render, so sync on its contents.
  const storedIp = value?.join(', ');
  useEffect(() => {
    setIp(storedIp);
  }, [storedIp]);
  // Clear all drops unwritten text too, even when the URL had no IP.
  const appliedClearCount = useRef(clearCount);
  useEffect(() => {
    if (appliedClearCount.current !== clearCount) {
      appliedClearCount.current = clearCount;
      setIp('');
      setIsValid(true);
    }
  }, [clearCount]);
  // Writes once the typing has settled. Keying on the typed text too means
  // retyping the last debounced value after a reset is still written.
  useEffect(() => {
    if (debouncedIpValue !== undefined && debouncedIpValue === ip) {
      const ipValue = debouncedIpValue.trim();
      const isIpValid = isValidIp(ipValue);

      setIsValid(ipValue.length === 0 || isIpValid);
      if (isIpValid) {
        const sanitizedIp = ipValue.split(ipDelimiter).map((ip) => {
          ip.trim();
          return ip;
        });
        setValue(sanitizedIp);
      }

      if (ipValue.length === 0) {
        setValue([]);
      }
    }
  }, [debouncedIpValue, ip]);

  return (
    <div
      data-testid={dataTestId}
      className={`relative inline-table h-[40px] flex-grow-0 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 antialiased focus:border-gray-300 dark:focus:border-gray-600 focus:ring-1 focus:ring-gray-500 dark:focus:ring-gray-400 focus:outline-none ${wrapperClassName}`}
    >
      <div
        className={`flex h-full w-full text-gray-600 dark:text-gray-400 placeholder-gray-400 dark:placeholder-gray-500 ${justifyCenter && 'justify-center'} items-center font-normal ${isValid ? '' : 'bg-error-background'}`}
      >
        <GlobeAltIcon className="ml-2 h-4 w-4" />
        <div className="flex-1">
          <IpInput placeholder={placeholder} ipValue={ip} setIpValue={setIp} />
        </div>

        {showClear && ip && (
          <button
            className="flex items-center justify-end self-stretch justify-self-stretch rounded-r-md px-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100"
            onClick={() => {
              setValue([]);
              setIp('');
              if (!isValid) {
                setIsValid(true);
              }
            }}
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};
